import type { ComponentProps, ComponentType } from 'react';
import { makeStyles } from '@material-ui/core/styles';

import { OpenApiDefinitionWidget } from '@backstage/plugin-api-docs';

import { patchOpenApi } from './patchOpenApi';

type SwaggerOAuthSystem = {
  initOAuth: (config: { usePkceWithAuthorizationCodeGrant: boolean }) => void;
};

type SwaggerOAuthRequest = {
  url?: string;
  body?: unknown;
};

// onComplete isn't in the widget's public prop types, but it forwards unknown props
// straight to Swagger UI, so widen the type to pass the PKCE initializer through.
type OpenApiWithPkceProps = ComponentProps<typeof OpenApiDefinitionWidget> & {
  onComplete?: (system: SwaggerOAuthSystem) => void;
};

const OpenApiDefinitionWithPkce = OpenApiDefinitionWidget as ComponentType<OpenApiWithPkceProps>;

// Entra registers the Swagger client as a public SPA, so the PKCE token exchange
// must not carry a client_secret even if one is entered in the Authorize dialog.
const stripClientSecretFromTokenRequest = (req: SwaggerOAuthRequest): SwaggerOAuthRequest => {
  if (typeof req.body === 'string' && req.body.includes('grant_type=authorization_code')) {
    req.body = req.body
      .split('&')
      .filter(param => !param.startsWith('client_secret='))
      .join('&');
  }
  return req;
};

// Swagger UI still renders the client_secret field under PKCE (masked as ******),
// which Entra's public SPA client doesn't use, so we annotate its label with info that it is not required.
const useStyles = makeStyles({
  root: {
    '& .swagger-ui .auth-container label[for^="client_secret"]::after': {
      content: '" (usually not required with PKCE used by Entra ID)"',
      fontWeight: 'normal',
      fontStyle: 'italic',
      opacity: 0.8,
    },
  },
});

const OpenApiWithPkceDefinition = ({ definition }: { definition: string }) => {
  const classes = useStyles();
  return (
    <div className={classes.root}>
      <OpenApiDefinitionWithPkce
        definition={patchOpenApi(definition)}
        requestInterceptor={stripClientSecretFromTokenRequest}
        onComplete={system => system.initOAuth({ usePkceWithAuthorizationCodeGrant: true })}
      />
    </div>
  );
};

export const OpenApiWithPkce = (definition: string) => (
  <OpenApiWithPkceDefinition definition={definition} />
);
